import * as React from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { API, CreatePayment, ICustomer as ICustomerDEPRECATED } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { message, Icon } from 'antd'
import { barcodeStore } from '../../../components/BarcodeStore'
import { parseErrorMsg } from '../../../utilities/helpers'
import { CustomerViewAndEditModal } from '../../../components/CustomerViewAndEditModal'
import { THEME_COLORS } from '@getgreenline/shared'
import { Button, Input, Select } from 'antd-v4'
import { UserOutlined } from '@ant-design/icons'
import { LoyaltyPoints } from '../../../components/LoyaltyPoints'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import { CustomersApi, ICustomer, customerSearchFilters } from '@getgreenline/customers'
import _ from 'lodash'
import './FindCustomerModal.scss'
import { appsyncApi } from '../../../appsync/graphql/api'
import { CustomerSearch } from '@getgreenline/customer-search'

// ICustomer in homi-shared (here renamed to ICustomerDEPRECATED) is different from ICustomer in greenline-shared.
// Please remove references to ICustomerDEPRECATED when removing Customer Search V1, in this file and in ExpandablePaymentDetails.tsx (for the addCustomerOnPayment function)
interface Props {
  show: boolean
  onHide: () => void
  onSelect: (customer: ICustomerDEPRECATED | ICustomer | CustomerSearch.API.GlobalCustomer) => void
  loadCustomer?: (customerId: string) => void
  currentCompanyStore?: CurrentCompanyStore
  canAddCustomers?: boolean
  canEditCustomer?: boolean
  canViewCustomerTotalSpend: boolean
  payment?: CreatePayment
  customerLocationId?: number
  isFromDashboard?: boolean // this is to temporary workaround with feature-toggle this can be removed and refactored once the feature-toggle `canUseParkedSaleEnhancements` is removed
}

interface State {
  query?: string
  foundCustomers?: ICustomerDEPRECATED[] | ICustomer[]
  foundGlobalCustomers?: CustomerSearch.API.GlobalCustomer[]
  selectedCustomer?: any
  searching: boolean
  showAddCustomerModal: boolean
  showSelectCustomerModal: boolean
  showCustomerDetailsModal: boolean
  selectedSearchFilter: string
}

const { Option } = Select

@inject('currentCompanyStore')
@observer
export class FindCustomerModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      query: undefined,
      foundCustomers: undefined,
      foundGlobalCustomers: undefined,
      selectedCustomer: undefined,
      searching: false,
      showAddCustomerModal: false,
      showSelectCustomerModal: false,
      showCustomerDetailsModal: false,
      selectedSearchFilter: localStorage.getItem('selectedCustomerSearchFilter') || 'phone',
    }
  }

  componentDidMount = async () => {
    setTimeout(() => {
      const input = document.getElementById('customer-search-input')
      if (input) {
        input.focus()
      }
    }, 200)

    barcodeStore.setBarcodeCallback(async (query: string) => {
      if (this.props.currentCompanyStore?.canUsePlatformCustomers) {
        this.searchCustomerV3(query)
      } else if (this.props.currentCompanyStore?.canUseCustomerSearchV2) {
        this.searchCustomerV2(query, true)
      } else {
        this.searchCustomer(query)
      }
    })

    await this.props.currentCompanyStore?.refreshLoyaltyPointsMultiplier()
  }

  componentWillUnmount = () => {
    barcodeStore.setBackToPrevBarcodeCallback()
  }

  // Using throttle to avoid making 2 API calls when scanning a barcode
  searchCustomer = _.throttle(
    async (query: string | undefined) => {
      if (!query) return

      const company = this.props.currentCompanyStore?.company
      if (!company) return

      try {
        await this.setState({
          query,
          searching: true,
        })
        const paginatedCustomers = await API.searchCustomers(company.id, query)
        this.setState({ foundCustomers: paginatedCustomers.customers })
      } catch (error) {
        message.error(parseErrorMsg(error) || 'An error occurred while loading customers')
      } finally {
        this.setState({ searching: false })
      }
    },
    500,
    { leading: false, trailing: true },
  )

  selectCustomer(customer: ICustomerDEPRECATED | ICustomer) {
    this.props.onSelect(customer)
  }

  closeWithoutSaving = () => {
    this.setState({
      selectedCustomer: undefined,
    })
    this.props.onHide()
  }

  isLoyaltyVisible = ({
    loyaltyTierId,
    creditBalance,
  }: {
    loyaltyTierId: number | null
    creditBalance: number
  }) => {
    if (!this.props.currentCompanyStore?.company?.permissions.canAccessLoyalty) {
      return false
    }

    if (loyaltyTierId === null) {
      return creditBalance > 0
    }

    return true
  }

  setCardIdAsSelectedSearchFilter = () => {
    this.setState({ selectedSearchFilter: 'cardId' })
    localStorage.setItem('selectedCustomerSearchFilter', 'cardId')
  }

  searchCustomerV2 = _.throttle(
    async (query: string | undefined, isBarcodeScanned?: boolean) => {
      if (!query) return

      if (isBarcodeScanned) {
        this.setCardIdAsSelectedSearchFilter()
      }

      const company = this.props.currentCompanyStore?.company
      if (!company) return

      try {
        this.setState({
          query,
          searching: true,
        })

        const { customers } = await CustomersApi.searchCustomersV2(company.id, {
          [this.state.selectedSearchFilter]: query,
          limit: 100,
          offset: 0,
        })
        this.setState({ foundCustomers: customers })
      } catch (error) {
        message.error('An error occurred while loading customers')
      } finally {
        this.setState({ searching: false })
      }
    },
    500,
    { leading: false, trailing: true },
  )

  // Using throttle to avoid making 2 API calls when scanning a barcode
  searchCustomerV3 = _.throttle(
    async (query: string | undefined) => {
      const company = this.props.currentCompanyStore?.company
      if (!company) return
      try {
        const response = await appsyncApi.getCustomersQuery({
          variables: {
            limit: 100,
            merchantId: `blc-${company?.id}`,
            query,
            nextPageCursor: null,
            prevPageCursor: null,
          },
        })
        this.setState({ foundGlobalCustomers: response?.data?.getCustomers?.customers || [] })
      } catch (error) {
        message.error(parseErrorMsg(error) || 'An error occurred while loading customers')
      } finally {
        this.setState({ searching: false })
      }
    },
    500,
    { leading: false, trailing: true },
  )

  render() {
    const { isFromDashboard } = this.props
    const { selectedSearchFilter } = this.state
    const textMuted: React.CSSProperties = { color: THEME_COLORS.BRAND.blackAlt }

    let foundCustomerSection: JSX.Element | undefined
    if (this.props.currentCompanyStore?.canUsePlatformCustomers) {
      foundCustomerSection = this.props.currentCompanyStore.company?.id ? (
        <FindGlobalCustomerRow
          companyId={this.props.currentCompanyStore.company.id}
          customers={this.state.foundGlobalCustomers || []}
          onSelect={this.props.onSelect}
          textMuted={textMuted}
          isLoyaltyVisible={this.isLoyaltyVisible}
        />
      ) : undefined
    } else {
      if (this.state.foundCustomers === undefined) {
        foundCustomerSection = <div style={textMuted}>Type your customer information above</div>
      } else if (this.state.foundCustomers.length === 0) {
        foundCustomerSection = <div style={textMuted}>No customers found</div>
      } else {
        foundCustomerSection = (
          <div>
            {this.state.foundCustomers.map((customer) => {
              return (
                <div
                  className='mb-2'
                  key={customer.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.selectCustomer(customer)}
                >
                  <a>
                    <span className='mr-1'>{customer.name}</span>
                  </a>

                  {this.isLoyaltyVisible(customer) && (
                    <div>
                      <small style={textMuted}>
                        {`${customer.loyaltyTierName || 'Loyalty'}: `}
                        <LoyaltyPoints
                          loyaltyBalance={customer.creditBalance}
                          showDollarEquivalent
                        />
                      </small>
                    </div>
                  )}

                  <div>
                    <small style={textMuted}>{customer.email || 'No email'}</small>
                  </div>
                  <div>
                    <small style={textMuted}>{customer.phone || 'No phone'}</small>
                  </div>
                  <div>
                    <small style={textMuted}>
                      Registered: {moment(customer.createDate).format('LLLL')}
                    </small>
                  </div>
                </div>
              )
            })}
          </div>
        )
      }
    }

    return (
      <Modal
        isOpen={this.props.show}
        toggle={() => this.props.onHide()}
        size='lg'
        autoFocus={false}
      >
        <ModalHeader toggle={() => this.props.onHide()}>Select customer</ModalHeader>
        <ModalBody className='text-xs-center'>
          <div className='form-group d-flex'>
            {this.props.currentCompanyStore?.canUsePlatformCustomers ? (
              <Input.Group compact size='large' className='customer-search-bar-v1'>
                <Input
                  id={'customer-search-input'}
                  style={{ width: '85%' }}
                  placeholder='Search customers by name, email, phone or card ID number'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    this.setState({ query: e.target.value })
                  }}
                  value={this.state.query}
                  onPressEnter={() => this.searchCustomerV3(this.state.query)}
                />
                <BlazeButton
                  buttonProps={{
                    type: 'primary',
                    size: 'large',
                    className: 'search-button',
                    loading: this.state.searching,
                    disabled: this.state.searching || !this.state.query,
                    onClick: () => this.searchCustomerV3(this.state.query),
                  }}
                  titleProps={{
                    title: 'Search',
                  }}
                />
              </Input.Group>
            ) : this.props.currentCompanyStore?.canUseCustomerSearchV2 ? (
              <Input.Group compact size='large' className='customer-search-bar-v2'>
                <Select
                  size='large'
                  style={{ width: '27%' }}
                  defaultValue={selectedSearchFilter}
                  value={selectedSearchFilter}
                  onChange={(value: string) => {
                    this.setState({ selectedSearchFilter: value })
                    localStorage.setItem('selectedCustomerSearchFilter', value)
                  }}
                >
                  {customerSearchFilters.map((filter) => (
                    <Option value={filter.value} key={filter.value}>
                      {filter.label}
                    </Option>
                  ))}
                </Select>
                <Input
                  id={'customer-search-input'}
                  style={{ width: '56%' }}
                  placeholder='Search customers'
                  value={this.state.query}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    this.setState({ query: e.target.value })
                  }}
                  onPressEnter={() => this.searchCustomerV2(this.state.query)}
                />
                <BlazeButton
                  buttonProps={{
                    type: 'primary',
                    size: 'large',
                    className: 'search-button',
                    loading: this.state.searching,
                    disabled: this.state.searching || !this.state.query,
                    onClick: () => this.searchCustomerV2(this.state.query),
                  }}
                  titleProps={{
                    title: 'Search',
                  }}
                />
              </Input.Group>
            ) : (
              <Input.Group compact size='large' className='customer-search-bar-v1'>
                <Input
                  id={'customer-search-input'}
                  style={{ width: '85%' }}
                  placeholder='Search customers by name, email, phone or card ID number'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    this.setState({ query: e.target.value })
                  }}
                  value={this.state.query}
                  onPressEnter={() => this.searchCustomer(this.state.query)}
                />
                <BlazeButton
                  buttonProps={{
                    type: 'primary',
                    size: 'large',
                    className: 'search-button',
                    loading: this.state.searching,
                    disabled: this.state.searching || !this.state.query,
                    onClick: () => this.searchCustomer(this.state.query),
                  }}
                  titleProps={{
                    title: 'Search',
                  }}
                />
              </Input.Group>
            )}

            <Button
              icon={<UserOutlined />}
              style={{ height: 'unset' }}
              disabled={!this.props.canAddCustomers}
              onClick={() => this.setState({ showAddCustomerModal: true })}
            >
              Add
            </Button>
          </div>

          <hr />

          {this.state.searching ? (
            <div style={textMuted}>
              <Icon type='loading' className='mr-2' /> searching customers...
            </div>
          ) : (
            foundCustomerSection
          )}
        </ModalBody>
        {this.state.showAddCustomerModal && !isFromDashboard && (
          <CustomerViewAndEditModal
            canViewCustomerTotalSpend={this.props.canViewCustomerTotalSpend}
            customerLocationId={this.props.customerLocationId}
            canSaveCustomer={this.props.canAddCustomers!}
            onSubmit={(customer) => {
              this.props.loadCustomer && this.props.loadCustomer(customer.id)
              this.props.onHide()
            }}
            onHide={() => this.setState({ showAddCustomerModal: false })}
            onCustomerUndelete={this.closeWithoutSaving}
          />
        )}
        {this.state.showCustomerDetailsModal && this.props.payment?.customerWithHistory && (
          <CustomerViewAndEditModal
            canViewCustomerTotalSpend={this.props.canViewCustomerTotalSpend}
            customerLocationId={this.props.customerLocationId}
            canSaveCustomer={this.props.canEditCustomer!}
            customerId={this.props.payment.customerWithHistory.customer.id}
            onSubmit={(customer) => {
              this.props.loadCustomer && this.props.loadCustomer(customer.id)
            }}
            onHide={() => this.setState({ showCustomerDetailsModal: false })}
          />
        )}

        {this.state.showAddCustomerModal && isFromDashboard && (
          <CustomerViewAndEditModal
            canSaveCustomer={true}
            canViewCustomerTotalSpend={this.props.canViewCustomerTotalSpend}
            onSubmit={async (customer) => {
              this.setState({ showAddCustomerModal: false })
              await this.props.onSelect(customer)
            }}
            onHide={() => this.setState({ showAddCustomerModal: false })}
          />
        )}
      </Modal>
    )
  }
}

interface IFindCustomerCardState {
  customer?: ICustomerDEPRECATED | ICustomer
}

interface IFindCustomerCardProps {
  customers: CustomerSearch.API.GlobalCustomer[]
  onSelect: (customer: ICustomerDEPRECATED | ICustomer) => void
  companyId: number
  textMuted: React.CSSProperties
  isLoyaltyVisible: (customer: { loyaltyTierId: number | null; creditBalance: number }) => boolean
}
class FindGlobalCustomerRow extends React.Component<
  IFindCustomerCardProps,
  IFindCustomerCardState
> {
  constructor(props: IFindCustomerCardProps) {
    super(props)
    this.state = {
      customer: undefined,
    }
  }
  render() {
    const { textMuted } = this.props
    let foundCustomerSection: JSX.Element | undefined
    if (this.props.customers === undefined) {
      foundCustomerSection = <div style={textMuted}>Type your customer information above</div>
    } else if (this.props.customers.length === 0) {
      foundCustomerSection = <div style={textMuted}>No customers found</div>
    } else {
      foundCustomerSection = (
        <div>
          {this.props.customers.map((customer) => {
            return (
              <div
                className='mb-2'
                key={customer.id}
                style={{ cursor: 'pointer' }}
                onClick={async () => {
                  const customerWithHistory = await API.getCustomerById(
                    this.props.companyId,
                    customer.id,
                  )
                  this.props.onSelect(customerWithHistory.customer)
                  this.setState({ customer: customerWithHistory.customer })
                }}
              >
                <a>
                  <span className='mr-1'>{`${customer.firstName} ${customer?.lastName}`}</span>
                </a>

                {this.state.customer &&
                  this.props.isLoyaltyVisible({
                    creditBalance: customer?.loyaltyPoints || 0,
                    loyaltyTierId: customer?.loyaltyTierId ? Number(customer?.loyaltyTierId) : null,
                  }) && (
                    <div>
                      <small style={textMuted}>
                        {`${customer.loyaltyTierName || 'Loyalty'}: `}
                        <LoyaltyPoints
                          loyaltyBalance={this.state.customer.creditBalance}
                          showDollarEquivalent
                        />
                      </small>
                    </div>
                  )}

                <div>
                  <small style={textMuted}>{customer.email || 'No email'}</small>
                </div>
                <div>
                  <small style={textMuted}>{customer.phone || 'No phone'}</small>
                </div>
                <div>
                  <small style={textMuted}>
                    Registered: {moment(customer.createDate).format('LLLL')}
                  </small>
                </div>
              </div>
            )
          })}
        </div>
      )
    }
    return foundCustomerSection
  }
}
